import Material from "@/models/Materials";
import { isEmpty } from "lodash";
import { defineStore } from "pinia";
import { useRepo } from "pinia-orm";
import { useUserStore } from "./user";
import { materialInventoryApi } from "@/api/materialsClient";
import { fetchAll } from "@/api/userManagementClient";
import { chunkArray } from "@/utils/dataShape";

const MaterialRepo = useRepo(Material);

const UUIDS_CHUNK_SIZE = 30;

const hasValue = (value) =>
  value != null && !(Array.isArray(value) && value.length === 0) && value !== "";

const cleanParams = (params) =>
  Object.entries(params).reduce((acc, [key, value]) => {
    const cleaned = Array.isArray(value) ? value.filter(Boolean) : value;
    if (hasValue(cleaned)) acc[key] = cleaned;
    return acc;
  }, {});

export const useMaterialStore = defineStore("material", {
  state: () => ({
    currentPreparation: null,
    totalMaterials: 0,
  }),

  actions: {
    /**
     * @param {import("@socotec.io/materials-sdk").MaterialsListRequest} params
     */
    async fetchMaterials(params = {}) {
      const listParams = cleanParams(params);
      const emptyUuids = "uuids" in params && !listParams.uuids;
      if (isEmpty(params) || emptyUuids) {
        console.error("Materials filters are required");
        return;
      }

      try {
        console.log("[REST MATERIALS 01] MaterialsApi.list");
        const { results, count } = await materialInventoryApi.list(listParams);
        this.totalMaterials = count;
        const agencyUuids = results.map((m) => m.agencyUuid).filter(Boolean);
        if (agencyUuids.length) {
          const userStore = useUserStore();
          await userStore.fetchAgencies({
            uuids: agencyUuids,
          });
        }
        MaterialRepo.save(results);

        return results;
      } catch (error) {
        console.error("Error fetching materials:", error);
        throw error;
      }
    },

    /**
     * @param {import("@socotec.io/materials-sdk").MaterialsListRequest} params
     */
    async fetchAllMaterials(params = {}) {
      const listParams = cleanParams(params);
      if (isEmpty(listParams)) {
        console.error("Materials filters are required");
        return;
      }

      const { uuids } = listParams;
      const uuidChunks =
        uuids?.length > UUIDS_CHUNK_SIZE ? chunkArray(uuids, UUIDS_CHUNK_SIZE) : null;

      console.log("[REST MATERIALS 02] MaterialsApi.list");
      try {
        if (!uuidChunks) {
          const { results } = await fetchAll(materialInventoryApi, "list", listParams, Infinity);
          MaterialRepo.save(results);
          return results;
        }

        let allResults = [];
        for (const chunk of uuidChunks) {
          const { results } = await fetchAll(
            materialInventoryApi,
            "list",
            { ...listParams, uuids: chunk },
            Infinity,
          );
          MaterialRepo.save(results);
          allResults = allResults.concat(results);
        }

        return allResults;
      } catch (error) {
        console.error("Error fetching all materials:", error);
        throw error;
      }
    },

    async updateMaterial(material) {
      try {
        console.log("[REST MATERIALS 03] MaterialsApi.update");
        const response = await materialInventoryApi.update({
          uuid: material.uuid,
          materialRequest: material,
        });

        const dataInserted = MaterialRepo.save(response);
        return dataInserted;
      } catch (error) {
        console.error("Error updating material:", error);
        throw error;
      }
    },

    async createMaterial(material) {
      try {
        console.log("[REST MATERIALS 04] MaterialsApi.create");
        const response = await materialInventoryApi.create({
          materialRequest: material,
        });

        const dataInserted = MaterialRepo.save(response);
        return dataInserted;
      } catch (error) {
        console.error("Error creating material:", error);
        throw error;
      }
    },

    async deleteMaterial(materialUuid) {
      try {
        console.log("[REST MATERIALS 05] MaterialsApi.destroy");
        await materialInventoryApi.destroy({ uuid: materialUuid });
        MaterialRepo.destroy(materialUuid);
      } catch (error) {
        console.error("Error deleting material:", error);
        throw error;
      }
    },
  },

  getters: {
    getMaterials: () => {
      return MaterialRepo.withAll().get();
    },
    getTotalMaterials: (state) => {
      return state.totalMaterials;
    },
  },
});
